<script setup>
import { computed, onMounted, ref, reactive, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import AiFocusAgentAPI from 'dashboard/api/aifocus/agent';

import PageLayout from 'dashboard/components-next/aifocus/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();

const agentId = computed(() => Number(route.params.agentId));
const agent = computed(() =>
  store.getters['aifocusAgents/getRecord'](agentId.value)
);

const uiFlags = useMapGetter('aifocusAgents/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const isUpdating = computed(() => uiFlags.value.isUpdating);

// State
const availableTools = ref([]);
const isLoadingTools = ref(false);

// Tools from agent
const agentTools = ref([]);
const customTools = ref({ http_tools: [], function_tools: [] });

// Modal state
const showAddToolModal = ref(false);
const showHttpToolModal = ref(false);
const editingTool = ref(null);

// HTTP Tool Form
const httpToolForm = reactive({
  name: '',
  description: '',
  method: 'GET',
  endpoint: '',
  headers: '',
  body_template: '',
  parameters: [],
});

const handleBackToSettings = () => {
  router.push({
    name: 'aifocus_agents_edit',
    params: {
      accountId: route.params.accountId,
      agentId: route.params.agentId,
    },
  });
};

const loadAvailableTools = async () => {
  try {
    isLoadingTools.value = true;
    const response = await AiFocusAgentAPI.getTools();
    if (response.data) {
      availableTools.value = response.data.tools || [];
    }
  } catch {
    // Use default tools
    availableTools.value = [
      {
        id: 'calculate',
        name: 'Calculator',
        description: 'Perform mathematical calculations',
        category: 'builtin',
      },
      {
        id: 'get_datetime',
        name: 'Date/Time',
        description: 'Get current date and time',
        category: 'builtin',
      },
      {
        id: 'process_json',
        name: 'JSON Processor',
        description: 'Parse and manipulate JSON data',
        category: 'builtin',
      },
    ];
  } finally {
    isLoadingTools.value = false;
  }
};

const updateAgentFromData = agentData => {
  if (!agentData) return;
  agentTools.value = agentData.agent_tools || [];
  customTools.value = agentData.custom_tools || {
    http_tools: [],
    function_tools: [],
  };
};

onMounted(async () => {
  // Validate agentId before making any API calls
  if (!agentId.value || isNaN(agentId.value)) {
    console.error('tools/Index.vue: Invalid agentId on mount:', agentId.value);
    return;
  }

  await store.dispatch('aifocusAgents/get');
  if (!agent.value) {
    await store.dispatch('aifocusAgents/show', agentId.value);
  }
  updateAgentFromData(agent.value);
  await loadAvailableTools();
});

// Watch agent changes - use Vue watch to avoid loops
watch(
  agent,
  newAgent => {
    if (newAgent) updateAgentFromData(newAgent);
  },
  { deep: false }
);

const isToolEnabled = toolId => {
  return agentTools.value.includes(toolId);
};

const toggleTool = async toolId => {
  if (!agentId.value || isNaN(agentId.value)) {
    useAlert('Erro: ID do agente invalido');
    return;
  }

  const newTools = isToolEnabled(toolId)
    ? agentTools.value.filter(t => t !== toolId)
    : [...agentTools.value, toolId];

  try {
    await store.dispatch('aifocusAgents/update', {
      id: agentId.value,
      agent_tools: newTools,
    });
    agentTools.value = newTools;
    useAlert('Ferramentas atualizadas com sucesso');
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar ferramentas');
  }
};

const openHttpToolModal = (tool = null) => {
  if (tool) {
    editingTool.value = tool;
    Object.assign(httpToolForm, tool);
    httpToolForm.headers = JSON.stringify(tool.headers || {}, null, 2);
  } else {
    editingTool.value = null;
    httpToolForm.name = '';
    httpToolForm.description = '';
    httpToolForm.method = 'GET';
    httpToolForm.endpoint = '';
    httpToolForm.headers = '{}';
    httpToolForm.body_template = '';
    httpToolForm.parameters = [];
  }
  showHttpToolModal.value = true;
};

const saveHttpTool = async () => {
  // Validate agentId before making API call
  if (!agentId.value || isNaN(agentId.value)) {
    useAlert('Erro: ID do agente invalido');
    return;
  }

  try {
    let headers = {};
    try {
      headers = JSON.parse(httpToolForm.headers || '{}');
    } catch {
      useAlert('Headers deve ser um JSON valido');
      return;
    }

    const toolData = {
      name: httpToolForm.name,
      description: httpToolForm.description,
      method: httpToolForm.method,
      endpoint: httpToolForm.endpoint,
      headers,
      body_template: httpToolForm.body_template,
      parameters: httpToolForm.parameters,
    };

    let httpTools = [...(customTools.value.http_tools || [])];

    if (editingTool.value) {
      const index = httpTools.findIndex(t => t.name === editingTool.value.name);
      if (index >= 0) {
        httpTools[index] = toolData;
      }
    } else {
      httpTools.push(toolData);
    }

    await store.dispatch('aifocusAgents/update', {
      id: agentId.value,
      custom_tools: {
        ...customTools.value,
        http_tools: httpTools,
      },
    });

    customTools.value.http_tools = httpTools;
    showHttpToolModal.value = false;
    useAlert('Ferramenta HTTP salva com sucesso');
  } catch (error) {
    useAlert(error?.message || 'Erro ao salvar ferramenta HTTP');
  }
};

const deleteHttpTool = async toolName => {
  // Validate agentId before making API call
  if (!agentId.value || isNaN(agentId.value)) {
    useAlert('Erro: ID do agente invalido');
    return;
  }

  if (!confirm(`Deseja excluir a ferramenta "${toolName}"?`)) return;

  try {
    const httpTools = customTools.value.http_tools.filter(
      t => t.name !== toolName
    );

    await store.dispatch('aifocusAgents/update', {
      id: agentId.value,
      custom_tools: {
        ...customTools.value,
        http_tools: httpTools,
      },
    });

    customTools.value.http_tools = httpTools;
    useAlert('Ferramenta excluida');
  } catch (error) {
    useAlert(error?.message || 'Erro ao excluir ferramenta');
  }
};

const addParameter = () => {
  httpToolForm.parameters.push({
    name: '',
    type: 'string',
    description: '',
    required: false,
  });
};

const removeParameter = index => {
  httpToolForm.parameters.splice(index, 1);
};

const getMethodColor = method => {
  const colors = {
    GET: 'bg-green-100 text-green-700',
    POST: 'bg-blue-100 text-blue-700',
    PUT: 'bg-yellow-100 text-yellow-700',
    DELETE: 'bg-red-100 text-red-700',
    PATCH: 'bg-purple-100 text-purple-700',
  };
  return colors[method] || 'bg-gray-100 text-gray-700';
};

const getToolIcon = category => {
  const icons = {
    builtin: 'i-lucide-wrench',
    http: 'i-lucide-globe',
    function: 'i-lucide-code',
    mcp: 'i-lucide-plug',
  };
  return icons[category] || 'i-lucide-tool';
};
</script>

<template>
  <PageLayout
    :is-fetching="isFetching || isLoadingTools"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #header>
      <div class="flex items-center justify-between w-full px-6 py-4">
        <div class="flex items-center gap-4">
          <Button
            icon="i-lucide-arrow-left"
            variant="ghost"
            size="sm"
            @click="handleBackToSettings"
          />
          <div>
            <h2 class="text-lg font-semibold text-n-slate-12">
              Ferramentas do Agente
            </h2>
            <p class="text-sm text-n-slate-11">
              Configure as ferramentas que o agente pode usar para executar
              acoes.
            </p>
          </div>
        </div>
        <Button
          label="Adicionar HTTP Tool"
          icon="i-lucide-plus"
          @click="openHttpToolModal()"
        />
      </div>
    </template>

    <template #body>
      <div class="px-6 pb-6">
        <!-- Agent Info -->
        <div
          v-if="agent"
          class="mb-6 p-4 bg-n-alpha-1 border border-n-weak rounded-xl"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-full bg-n-alpha-2 flex items-center justify-center"
            >
              <span class="i-lucide-bot text-xl text-n-slate-11" />
            </div>
            <div>
              <p class="font-medium text-n-slate-12">{{ agent.name }}</p>
              <p class="text-xs text-n-slate-10">
                {{ agentTools.length }} ferramentas ativas
              </p>
            </div>
          </div>
        </div>

        <!-- Built-in Tools Section -->
        <div class="mb-8">
          <h3
            class="text-md font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
          >
            <span class="i-lucide-wrench text-n-slate-11" />
            Ferramentas Integradas
          </h3>
          <div class="grid gap-3">
            <div
              v-for="tool in availableTools"
              :key="tool.id"
              class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl hover:border-n-slate-6 transition-colors"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-lg bg-n-alpha-2 flex items-center justify-center"
                >
                  <span
                    class="text-xl text-n-slate-11"
                    :class="[getToolIcon(tool.category)]"
                  />
                </div>
                <div>
                  <p class="font-medium text-n-slate-12">{{ tool.name }}</p>
                  <p class="text-xs text-n-slate-10">{{ tool.description }}</p>
                </div>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  :checked="isToolEnabled(tool.id)"
                  class="sr-only peer"
                  @change="toggleTool(tool.id)"
                />
                <div
                  class="w-11 h-6 bg-n-alpha-3 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-n-brand/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-n-brand"
                />
              </label>
            </div>
          </div>
        </div>

        <!-- HTTP Tools Section -->
        <div class="mb-8">
          <h3
            class="text-md font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
          >
            <span class="i-lucide-globe text-n-slate-11" />
            HTTP Tools (APIs Externas)
          </h3>

          <div
            v-if="
              !customTools.http_tools || customTools.http_tools.length === 0
            "
            class="flex flex-col items-center justify-center py-12 text-center bg-n-alpha-1 border border-dashed border-n-weak rounded-xl"
          >
            <div
              class="w-12 h-12 mb-3 rounded-full bg-n-alpha-2 flex items-center justify-center"
            >
              <span class="i-lucide-globe text-2xl text-n-slate-10" />
            </div>
            <h4 class="text-md font-medium text-n-slate-12 mb-1">
              Nenhuma HTTP Tool configurada
            </h4>
            <p class="text-sm text-n-slate-11 mb-4 max-w-md">
              Adicione ferramentas HTTP para permitir que o agente chame APIs
              externas.
            </p>
            <Button
              label="Adicionar HTTP Tool"
              icon="i-lucide-plus"
              variant="secondary"
              @click="openHttpToolModal()"
            />
          </div>

          <div v-else class="grid gap-3">
            <div
              v-for="tool in customTools.http_tools"
              :key="tool.name"
              class="flex items-center justify-between p-4 bg-n-solid-1 border border-n-weak rounded-xl hover:border-n-slate-6 transition-colors"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 rounded-lg bg-n-alpha-2 flex items-center justify-center"
                >
                  <span class="i-lucide-globe text-xl text-n-slate-11" />
                </div>
                <div>
                  <div class="flex items-center gap-2">
                    <p class="font-medium text-n-slate-12">{{ tool.name }}</p>
                    <span
                      class="px-2 py-0.5 text-xs font-medium rounded"
                      :class="getMethodColor(tool.method)"
                    >
                      {{ tool.method }}
                    </span>
                  </div>
                  <p class="text-xs text-n-slate-10 truncate max-w-md">
                    {{ tool.endpoint }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <Button
                  icon="i-lucide-pencil"
                  variant="ghost"
                  size="sm"
                  @click="openHttpToolModal(tool)"
                />
                <Button
                  icon="i-lucide-trash-2"
                  variant="ghost"
                  size="sm"
                  color="ruby"
                  @click="deleteHttpTool(tool.name)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Info Card -->
        <div class="p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div class="flex gap-3">
            <span class="i-lucide-info text-blue-600 text-xl flex-shrink-0" />
            <div>
              <h4 class="font-medium text-blue-900 mb-1">
                Sobre as Ferramentas
              </h4>
              <p class="text-sm text-blue-700">
                Ferramentas permitem que o agente execute acoes como calculos,
                consultas a APIs, ou manipulacao de dados. O agente decide
                automaticamente quando usar cada ferramenta baseado no contexto
                da conversa.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- HTTP Tool Modal -->
      <div
        v-if="showHttpToolModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="showHttpToolModal = false"
      >
        <div
          class="bg-n-solid-2 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        >
          <div
            class="px-6 py-4 border-b border-n-weak sticky top-0 bg-n-solid-2 z-10"
          >
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{ editingTool ? 'Editar' : 'Adicionar' }} HTTP Tool
            </h3>
            <p class="text-sm text-n-slate-11">
              Configure uma ferramenta para chamar APIs externas.
            </p>
          </div>

          <div class="p-6 space-y-4">
            <Input
              v-model="httpToolForm.name"
              label="Nome da Ferramenta"
              placeholder="get_weather"
              message="Use snake_case sem espacos"
            />

            <Input
              v-model="httpToolForm.description"
              label="Descricao"
              placeholder="Obtem a previsao do tempo para uma cidade"
            />

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                Metodo HTTP
              </label>
              <select
                v-model="httpToolForm.method"
                class="w-full px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>

            <Input
              v-model="httpToolForm.endpoint"
              label="Endpoint URL"
              placeholder="https://api.example.com/weather"
            />

            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                Headers (JSON)
              </label>
              <textarea
                v-model="httpToolForm.headers"
                rows="3"
                class="w-full px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12 font-mono text-sm"
                placeholder='{"Authorization": "Bearer {{api_key}}"}'
              />
            </div>

            <div
              v-if="httpToolForm.method !== 'GET'"
              class="flex flex-col gap-2"
            >
              <label class="text-sm font-medium text-n-slate-12">
                Body Template (JSON)
              </label>
              <textarea
                v-model="httpToolForm.body_template"
                rows="3"
                class="w-full px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12 font-mono text-sm"
                placeholder='{"city": "{{city}}", "units": "metric"}'
              />
            </div>

            <!-- Parameters -->
            <div class="flex flex-col gap-3">
              <div class="flex items-center justify-between">
                <label class="text-sm font-medium text-n-slate-12">
                  Parametros
                </label>
                <Button
                  label="Adicionar"
                  icon="i-lucide-plus"
                  variant="ghost"
                  size="xs"
                  @click="addParameter"
                />
              </div>

              <div
                v-for="(param, index) in httpToolForm.parameters"
                :key="index"
                class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg space-y-2"
              >
                <div class="flex items-center gap-2">
                  <input
                    v-model="param.name"
                    placeholder="Nome"
                    class="flex-1 px-2 py-1 bg-n-solid-1 border border-n-weak rounded text-sm text-n-slate-12"
                  />
                  <select
                    v-model="param.type"
                    class="px-2 py-1 bg-n-solid-1 border border-n-weak rounded text-sm text-n-slate-12"
                  >
                    <option value="string">String</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="array">Array</option>
                    <option value="object">Object</option>
                  </select>
                  <label
                    class="flex items-center gap-1 text-sm text-n-slate-11"
                  >
                    <input v-model="param.required" type="checkbox" />
                    Obrigatorio
                  </label>
                  <Button
                    icon="i-lucide-x"
                    variant="ghost"
                    size="xs"
                    @click="removeParameter(index)"
                  />
                </div>
                <input
                  v-model="param.description"
                  placeholder="Descricao do parametro"
                  class="w-full px-2 py-1 bg-n-solid-1 border border-n-weak rounded text-sm text-n-slate-12"
                />
              </div>

              <p
                v-if="httpToolForm.parameters.length === 0"
                class="text-sm text-n-slate-10 text-center py-2"
              >
                Nenhum parametro definido
              </p>
            </div>
          </div>

          <div
            class="px-6 py-4 border-t border-n-weak flex justify-end gap-3 sticky bottom-0 bg-n-solid-2"
          >
            <Button
              label="Cancelar"
              variant="secondary"
              @click="showHttpToolModal = false"
            />
            <Button
              label="Salvar"
              :is-loading="isUpdating"
              @click="saveHttpTool"
            />
          </div>
        </div>
      </div>
    </template>
  </PageLayout>
</template>
